<script setup lang="ts">
    import KFormFieldDisplay from "@/components/forms/displays/FormFieldDisplay/KFormFieldDisplay.vue";
    import { FORM_HELPERS } from "@/components/forms/Form/helpers";
    import KInnerSection from "@/components/generic/KInnerSection.vue";
    import KSection from "@/components/generic/KSection.vue";
    import KTag from "@/components/KTag.vue";
    import { useStore } from "@/store";
    import moment from "moment";
    import { computed, inject, nextTick, onMounted, ref, toRefs } from "vue";

    const store = useStore();

    const props = withDefaults(
        defineProps<{
            hasSelectedLakemedel: boolean;
            receptforskrivningId?: string;
            fornya?: boolean;
        }>(),
        {
            fornya: false,
        }
    );

    const { receptforskrivningId, fornya } = toRefs(props);

    const getRequired = computed(() => {
        return fornya.value ? "" : "required";
    });

    const patient = computed(() => store.getters["patientData/patient"]);

    const lakemedelId = ref<string | null>(
        store.getters["receptforskrivning/receptforskrivningById"]({
            id: receptforskrivningId?.value,
        })?.lakemedelId
    );
    const formansvillkorUppfyllda = ref(false);
    const formansvillkorSmittkyddslagen = ref(false);
    const formansvillkorBerattigad = ref<boolean | null>(patient?.value.careCoverage);

    const uttagsdatumInnerSection = ref();
    const kommentarInnerSection = ref();

    const formHelpers = inject<{
        getFieldValue: (props: { name: string }) => unknown;
    } | null>(FORM_HELPERS, null);

    defineExpose({
        formListeners: [
            {
                fieldNames: ["lakemedelId"],
                action: ({ values }) => {
                    lakemedelId.value = values?.lakemedelId ? (values.lakemedelId as string) : null;
                },
            },
            {
                fieldNames: [
                    "formansvillkor_for_lakemedel_uppfyllda",
                    "forskrivning_enligt_smittskyddslagen",
                    "formansberattigad_patient",
                ],
                runAtInitial: true,
                action: ({ values }) => {
                    formansvillkorUppfyllda.value = !!values?.formansvillkor_for_lakemedel_uppfyllda;
                    formansvillkorSmittkyddslagen.value = !!values?.forskrivning_enligt_smittskyddslagen;
                    formansvillkorBerattigad.value = !!values?.formansberattigad_patient;
                },
            },
            {
                fieldNames: ["mall"],
                action: async ({ values }) => {
                    // Öppna sektionen vid val av mall
                    if (values.mall) {
                        await toggleInnerSectionsWithValue();
                    }
                },
            },
        ] as Array<{
            fieldNames: string[];
            action: ({ values }: { values: { [key: string]: unknown } }) => void;
        }>,
    });

    const lakemedel = computed(() =>
        store.getters["lakemedel/lakemedelById"]({ id: lakemedelId.value, displayMode: true })
    );

    /**
     * Expanderar inner sections som har data satt. Gäller vid inläsning
     */
    const toggleInnerSectionsWithValue = async () => {
        await nextTick();

        const tid_mellan_uttag = formHelpers?.getFieldValue({ name: "tid_mellan_uttag" });
        const tidsenhet = formHelpers?.getFieldValue({ name: "tidsenhet" });
        const forskrivarens_kommentar = formHelpers?.getFieldValue({ name: "forskrivarens_kommentar" });

        if (tid_mellan_uttag || tidsenhet) {
            uttagsdatumInnerSection?.value?.toggle?.();
        }

        if (forskrivarens_kommentar) {
            kommentarInnerSection?.value?.toggle?.();
        }
    };

    const fetchTidsenheter = async () => {
        await store.dispatch("nll/fetchTidsenheter", {});

        return store.getters["nll/tidsenheterSubset"]({ subsetName: "default" });
    };

    onMounted(async () => {
        await toggleInnerSectionsWithValue();
    });
</script>

<template>
    <KSection
        :expanded="hasSelectedLakemedel"
        name="expedieringsdetaljer"
        heading="Expedieringsdetaljer"
        :collapsible="hasSelectedLakemedel"
        :number="2"
    >
        <div class="form-content readable-disabled">
            <div class="form-section span-all">
                <KFormFieldDisplay
                    name="forpackning"
                    label="Förpackning"
                    :validation="getRequired"
                    :value="lakemedel?.forpackning"
                    showEmptyValue
                />
                <KNumber
                    name="antal_uttag"
                    label="Antal uttag"
                    :validation="getRequired + '|minvalue:1'"
                    :disabled="fornya"
                />
                <KNumber
                    name="forpackningar_per_uttag"
                    label="Förpackningar per uttag"
                    :validation="getRequired + '|minvalue:1'"
                    :disabled="fornya"
                />
                <KDateTimepicker
                    name="sista_giltighetsdag"
                    label="Sista giltighetsdag"
                    validation="required"
                    :maxDate="moment().add(1, 'year').toDate()"
                />
                <KCheckbox name="startforpackning" label="Startförpackning" valueType="boolean" :disabled="fornya" />
                <KCheckbox
                    name="substitution_tillaten"
                    label="Substitution tillåten"
                    valueType="boolean"
                    :disabled="fornya"
                />
            </div>
            <div class="span-two">
                <KText
                    v-if="fornya"
                    name="beskrivning_handelseorsak"
                    label="Beskrivning händelseorsak"
                    suffixText="Beskriv orsak till förnyelse"
                />
            </div>
            <KInnerSection
                ref="uttagsdatumInnerSection"
                name="uttagsdatum-inner-section"
                hiddenContent="Uttagsdatum"
                visibleContent="Uttagsdatum"
            >
                <div class="form-content">
                    <KNumber
                        name="tid_mellan_uttag"
                        label="Expedieringsintervall"
                        validation="minvalue:0"
                        :disabled="fornya"
                    />
                    <KAutocomplete name="tidsenhet" label="Tidsenhet" disabled :options="fetchTidsenheter" />
                    <KDateTimepicker name="forsta_uttag_fore" label="Första uttag före" />
                </div>
            </KInnerSection>
            <KInnerSection
                ref="kommentarInnerSection"
                name="kommentar-inner-section"
                hiddenContent="Kommentar från förskrivare"
                visibleContent="Kommentar från förskrivare"
            >
                <KText
                    name="forskrivarens_kommentar"
                    label="Förskrivarens kommentar"
                    type="textarea"
                    class="span-two"
                    suffixText="Kommentar från förskrivare till apotek"
                />
            </KInnerSection>
            <KGroup name="formansvillkor" label="Förmånsvillkor" class="span-all" freeFlow>
                <KCheckbox
                    name="forskrivning_enligt_smittskyddslagen"
                    label="Förskrivning enligt smittskyddslagen"
                    class="span-two"
                    valueType="boolean"
                    :disabled="fornya"
                />
                <KCheckbox
                    name="formansberattigad_patient"
                    label="Förmånsberättigad patient"
                    valueType="boolean"
                    :disabled="fornya"
                />
                <KCheckbox
                    v-if="lakemedel?.formansrestriktion"
                    name="formansvillkor_for_lakemedel_uppfyllda"
                    label="Förmånsvillkor för läkemedel uppfyllda"
                    valueType="boolean"
                />
            </KGroup>
            <KGroup name="formansvillkor-icons" class="span-all" freeFlow>
                <!-- Läkemedel subventionerat enligt smittskyddslagen -->
                <KTag v-if="formansvillkorSmittkyddslagen" variant="approval" size="large">
                    <span>Läkemedel subventionerat enligt smittskyddslagen</span>
                </KTag>
                <!-- Patienten är förmånsberättigad (eller ej) -->
                <KTag v-if="formansvillkorBerattigad" variant="approval" size="large">
                    <span>Patienten är förmånsberättigad</span>
                </KTag>
                <KTag v-else variant="warning" size="large">
                    <span>Patienten är ej förmånsberättigad</span>
                </KTag>
                <!-- Om läkemedlet har förmånsretriktioner. Förmånsvillkor för läkemedel uppfyllda (eller ej) -->
                <KTag v-if="lakemedel?.formansrestriktion && formansvillkorUppfyllda" variant="approval" size="large">
                    <span>Förmånsvillkor för läkemedel uppfyllda</span>
                </KTag>
                <KTag v-else-if="lakemedel?.formansrestriktion" variant="warning" size="large">
                    <span>Förmånsvillkor för läkemedel ej uppfyllda</span>
                </KTag>
                <!-- Om läkemedlet har förmånsretriktioner.  -->
                <KTag v-if="lakemedel?.formansrestriktion" variant="info" size="large">
                    <span>Läkemedlet är förmånsberättigat med förmånsbegränsning:</span>
                    <span>{{ lakemedel.formansrestriktion }}</span>
                </KTag>
            </KGroup>
        </div>
    </KSection>
</template>

<style scoped lang="scss">
    .expedieringsdetaljer {
    }
</style>
